import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, ArrowLeft, Plus, Edit2, Trash2, Save, X,
  ChevronLeft, ChevronRight, Check, AlertCircle,
  Building, Shield, Users, Phone, ShoppingCart, FileText,
  CreditCard, TrendingDown, MoreHorizontal, Landmark, PiggyBank
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const ICON_MAP = {
  'building': Building,
  'shield': Shield,
  'user-shield': Shield,
  'users': Users,
  'landmark': Landmark,
  'piggy-bank': PiggyBank,
  'phone': Phone,
  'shopping-cart': ShoppingCart,
  'file-text': FileText,
  'credit-card': CreditCard,
  'trending-down': TrendingDown,
  'more-horizontal': MoreHorizontal,
  'folder': FileText
};

export default function MonthlyChargesPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [charges, setCharges] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', type: 'fixe', default_amount: '' });
  
  // Édition inline
  const [editingCharge, setEditingCharge] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token, selectedMonth]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [catResponse, chargesResponse] = await Promise.all([
        axios.get(`${API}/api/monthly-charges/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/monthly-charges/month/${selectedMonth}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCategories(catResponse.data);
      setCharges(chargesResponse.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const saveCharge = async (categoryId, amount, paid = false) => {
    try {
      await axios.post(`${API}/api/monthly-charges`, {
        category_id: categoryId,
        month: selectedMonth,
        amount: parseFloat(amount) || 0,
        paid: paid
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Charge enregistrée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const togglePaid = async (charge) => {
    await saveCharge(charge.category_id, charge.amount, !charge.paid);
  };

  const saveCategory = async () => {
    if (!categoryForm.name) {
      toast.error('Veuillez saisir un nom');
      return;
    }
    
    try {
      if (editingCategory) {
        await axios.put(`${API}/api/monthly-charges/categories/${editingCategory.id}`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie modifiée');
      } else {
        await axios.post(`${API}/api/monthly-charges/categories`, categoryForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie créée');
      }
      
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', type: 'fixe', default_amount: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    
    try {
      await axios.delete(`${API}/api/monthly-charges/categories/${catId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${newMonth.toString().padStart(2, '0')}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return `${months[parseInt(month)]} ${year}`;
  };

  // Calculs
  const totalFixes = charges.filter(c => c.category_type === 'fixe').reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalVariables = charges.filter(c => c.category_type === 'variable').reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalCharges = totalFixes + totalVariables;
  const totalPaid = charges.filter(c => c.paid).reduce((sum, c) => sum + (c.amount || 0), 0);
  const totalUnpaid = totalCharges - totalPaid;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => {
              sessionStorage.setItem('reports_previous_path', '/monthly-charges');
              navigate('/reports');
            }} variant="ghost" size="sm" className="text-white/70">
              <ArrowLeft className="w-4 h-4 mr-1" /> Rapports
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <FileText className="w-6 h-6 text-orange-400" />
              Charges Mensuelles
            </h1>
          </div>
          <Button 
            onClick={() => {
              sessionStorage.removeItem('reports_authenticated');
              navigate('/');
            }} 
            variant="ghost" 
            size="sm" 
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10"
          >
            <Home className="w-4 h-4 mr-1" />
            Quitter Rapports
          </Button>
        </div>

        {/* Navigation Mois */}
        <div className="flex items-center justify-center gap-4 mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <Button onClick={() => navigateMonth(-1)} variant="ghost" size="sm" className="text-white/70">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-xl font-semibold text-white min-w-[200px] text-center">
            {formatMonth(selectedMonth)}
          </div>
          <Button onClick={() => navigateMonth(1)} variant="ghost" size="sm" className="text-white/70">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
            <div className="text-blue-300 text-sm mb-1">Charges Fixes</div>
            <div className="text-2xl font-bold text-white">{formatCurrency(totalFixes)}</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
            <div className="text-purple-300 text-sm mb-1">Charges Variables</div>
            <div className="text-2xl font-bold text-white">{formatCurrency(totalVariables)}</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 rounded-xl p-4">
            <div className="text-orange-300 text-sm mb-1">Total Charges</div>
            <div className="text-2xl font-bold text-white">{formatCurrency(totalCharges)}</div>
          </div>
          
          <div className={`bg-gradient-to-br ${totalUnpaid > 0 ? 'from-red-500/20 to-red-600/10 border-red-500/30' : 'from-green-500/20 to-green-600/10 border-green-500/30'} border rounded-xl p-4`}>
            <div className={`text-sm mb-1 ${totalUnpaid > 0 ? 'text-red-300' : 'text-green-300'}`}>
              {totalUnpaid > 0 ? 'Non payé' : 'Tout payé'}
            </div>
            <div className="text-2xl font-bold text-white">
              {totalUnpaid > 0 ? formatCurrency(totalUnpaid) : '✓'}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Détail des charges</h2>
          <Button 
            onClick={() => {
              setEditingCategory(null);
              setCategoryForm({ name: '', type: 'fixe', default_amount: '' });
              setShowCategoryModal(true);
            }}
            size="sm"
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="w-4 h-4 mr-1" /> Catégorie
          </Button>
        </div>

        {/* Liste des charges */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden">
          {/* En-tête */}
          <div className="grid grid-cols-12 gap-4 p-4 bg-white/5 border-b border-white/10 text-sm font-medium text-white/60">
            <div className="col-span-5">Catégorie</div>
            <div className="col-span-2 text-center">Type</div>
            <div className="col-span-2 text-right">Montant</div>
            <div className="col-span-2 text-center">Payé</div>
            <div className="col-span-1 text-center">Actions</div>
          </div>
          
          {charges.length === 0 ? (
            <div className="p-12 text-center text-white/50">
              Aucune charge pour ce mois
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {charges.map((charge, index) => {
                const IconComponent = ICON_MAP[categories.find(c => c.id === charge.category_id)?.icon] || FileText;
                
                return (
                  <div key={charge.category_id || index} className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-white/5 transition">
                    <div className="col-span-5 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        charge.category_type === 'fixe' ? 'bg-blue-500/20' : 'bg-purple-500/20'
                      }`}>
                        <IconComponent className={`w-5 h-5 ${
                          charge.category_type === 'fixe' ? 'text-blue-400' : 'text-purple-400'
                        }`} />
                      </div>
                      <div>
                        <div className="font-medium text-white">{charge.category_name}</div>
                      </div>
                    </div>
                    
                    <div className="col-span-2 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        charge.category_type === 'fixe' 
                          ? 'bg-blue-500/20 text-blue-300' 
                          : 'bg-purple-500/20 text-purple-300'
                      }`}>
                        {charge.category_type === 'fixe' ? 'Fixe' : 'Variable'}
                      </span>
                    </div>
                    
                    <div className="col-span-2 text-right">
                      {editingCharge === charge.category_id ? (
                        <div className="flex items-center gap-1 justify-end">
                          <Input
                            type="number"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 h-8 bg-white/10 border-white/20 text-white text-right text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                saveCharge(charge.category_id, editValue, charge.paid);
                                setEditingCharge(null);
                              } else if (e.key === 'Escape') {
                                setEditingCharge(null);
                              }
                            }}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-green-400"
                            onClick={() => {
                              saveCharge(charge.category_id, editValue, charge.paid);
                              setEditingCharge(null);
                            }}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className="font-medium text-white cursor-pointer hover:text-orange-400"
                          onClick={() => {
                            setEditingCharge(charge.category_id);
                            setEditValue(charge.amount?.toString() || '0');
                          }}
                        >
                          {formatCurrency(charge.amount)}
                        </div>
                      )}
                    </div>
                    
                    <div className="col-span-2 text-center">
                      <button
                        onClick={() => togglePaid(charge)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                          charge.paid 
                            ? 'bg-green-500 text-white' 
                            : 'bg-white/10 text-white/50 hover:bg-white/20'
                        }`}
                      >
                        {charge.paid ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    <div className="col-span-1 flex justify-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-white/60 hover:text-blue-400"
                        onClick={() => {
                          const cat = categories.find(c => c.id === charge.category_id);
                          if (cat) {
                            setEditingCategory(cat);
                            setCategoryForm({
                              name: cat.name,
                              type: cat.type,
                              default_amount: cat.default_amount?.toString() || ''
                            });
                            setShowCategoryModal(true);
                          }
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-white/60 hover:text-red-400"
                        onClick={() => deleteCategory(charge.category_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Catégorie */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white">
                    {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                  </h2>
                  <Button onClick={() => setShowCategoryModal(false)} variant="ghost" size="sm" className="text-gray-400">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Nom *</label>
                    <Input
                      value={categoryForm.name}
                      onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                      placeholder="Ex: Électricité, Abonnement..."
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Type</label>
                    <select
                      value={categoryForm.type}
                      onChange={(e) => setCategoryForm({...categoryForm, type: e.target.value})}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="fixe">Charge fixe (mensuelle)</option>
                      <option value="variable">Charge variable</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-white/80 mb-1 block">Montant par défaut</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={categoryForm.default_amount}
                      onChange={(e) => setCategoryForm({...categoryForm, default_amount: e.target.value})}
                      placeholder="0.00"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button onClick={() => setShowCategoryModal(false)} variant="outline" className="flex-1 border-white/20 text-white">
                    Annuler
                  </Button>
                  <Button onClick={saveCategory} className="flex-1 bg-orange-500 hover:bg-orange-600">
                    <Save className="w-4 h-4 mr-2" />
                    {editingCategory ? 'Modifier' : 'Créer'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
